 import React, { useState, useEffect, CSSProperties } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  fetchUserMerchants,
  selectMerchants,
  selectMerchantLoading,
  selectMerchantError,
  Merchant,
} from "../../../redux/merchantSlice";
import { AppDispatch, RootState } from "../../../redux/store";
import axios from "axios";
import { Variants } from "framer-motion";
import { getAuth } from "firebase/auth";

/* ─── PALETTE ─── */
const IVORY    = "#F5F0EB";
const CREAM    = "#EDE8E1";
const STONE    = "#D4CFC8";
const WARMGRAY = "#9B958D";
const DUSK     = "#6B6560";
const NIGHT    = "#1A1714";
const PURE_W   = "#FFFFFF";
const PURE_B   = "#000000";
const ROUGE    = "#C8303A";
const SAGE     = "#4A7C59";
const AMBER    = "#B8860B";

/* ─── FONT INJECTION ─── */
const fontHref =
  "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Libre+Franklin:wght@300;400;500;600;700&display=swap";
if (typeof document !== "undefined" && !document.head.querySelector(`link[href="${fontHref}"]`)) {
  const link = document.createElement("link");
  link.rel   = "stylesheet";
  link.href  = fontHref;
  document.head.appendChild(link);
}

/* ─── ANIMATION VARIANTS ─── */
const cardVariant: Variants = {
  hidden:  { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  }),
};

const modalOverlay: Variants = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1 },
  exit:    { opacity: 0 },
};

const modalContent: Variants = {
  hidden:  { opacity: 0, scale: 0.94, y: 20 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] } },
  exit:    { opacity: 0, scale: 0.96, y: 10 },
};

/* ─── STYLES ─── */
const serif: CSSProperties = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const sans: CSSProperties  = { fontFamily: "'Libre Franklin', 'Helvetica Neue', sans-serif" };

/* ─── KEY LABELS ─── */
const KEY_LABELS = [
  { label: "Publishable Key (Live)", key: "publishable_key_live" },
  { label: "Publishable Key (Test)", key: "publishable_key_test" },
  { label: "Secret Key (Live)",      key: "secret_key_live" },
  { label: "Secret Key (Test)",      key: "secret_key_test" },
  { label: "Webhook Secret (Live)",  key: "webhook_secret_live" },
  { label: "Webhook Secret (Test)",  key: "webhook_secret_test" },
  { label: "Restricted Key (Live)",  key: "restricted_key_live" },
  { label: "Restricted Key (Test)",  key: "restricted_key_test" },
];

/* ═══════════════════════════════════════
   MAIN COMPONENT
   Route: /organizationoverview/:userId
   ═══════════════════════════════════════ */
export default function OrganizationOverview() {
  const dispatch   = useDispatch<AppDispatch>();
 const navigate = useNavigate();
  // ─── userId comes from route params — NOT merchantId ───
  const { userId } = useParams<{ userId: string }>();

  // ─── All auth from Redux — NO direct Firebase calls ───
  const user       = useSelector((s: RootState) => s.auth.user);
  const merchants  = useSelector(selectMerchants);
  const loading    = useSelector(selectMerchantLoading);
  const storeError = useSelector(selectMerchantError);

  const [fetched, setFetched]           = useState(false);
  const [isMobile, setIsMobile]         = useState(typeof window !== "undefined" ? window.innerWidth < 800 : false);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [keysModal, setKeysModal]       = useState<{ merchantId: string; accountId: string; keys: Record<string, string> } | null>(null);
  const [copied, setCopied]             = useState<string | null>(null);
  const [error, setError]               = useState<string | null>(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 800);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ─── Fetch merchants by userId from URL param ───
  // Each merchant doc has a `userId` field that matches the Firebase Auth UID
  useEffect(() => {
    if (userId && !fetched) {
      dispatch(fetchUserMerchants(userId))
        .unwrap()
        .then(() => setFetched(true))
        .catch((e) => { setError(e?.toString() || "Failed to load."); setFetched(true); });
    }
  }, [userId, dispatch, fetched]);

  /* ─── Generate API keys ─── */
  /* ─── Generate API keys ─── */
  const handleGenerateKeys = async (merchant: Merchant) => {
    /*
    if (!user?.userId) {
      setError("Please sign in.");
      return;
    }
    if (merchant.keysGenerated) {
      setError("Keys already generated for this business. Use key rotation instead.");
      return;
    }
    */
    
    setGeneratingId(merchant.merchantId);
    setError(null);

    try {
      // Use the actual Firebase Auth instance to get a fresh token
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) throw new Error("Could not retrieve a valid authentication token.");

      const token = await currentUser.getIdToken(true);

      const { data } = await axios.post(
"https://europe-west3-cosmoola.cloudfunctions.net/registerMerchant",        { userId: merchant.userId || currentUser.uid, merchantId: merchant.merchantId },
        { headers: { Authorization: `Bearer ${token /* token */}` } }
      );

      setKeysModal({ merchantId: data.merchantId, accountId: data.accountId, keys: data.keys });
    } catch (err: any) {
      console.error("Error generating keys:", err);
      if (err.response?.status === 401) setError("Unauthorized. Please sign in again or check your account permissions.");
      else setError(err?.response?.data?.message || err?.toString() || "Failed to generate keys.");
    } finally {
      setGeneratingId(null);
    }
  };


  /* ─── Copy handler ─── */
  const copyToClipboard = (key: string, value: string) => {
    if (!value || value === "—") return;
    navigator.clipboard.writeText(value).then(() => setCopied(key)).catch(() => {});
    setTimeout(() => setCopied(null), 2000);
  };

  /* ═══ RENDER ═══ */
  return (
    <div style={{ minHeight: "100vh", background: IVORY, ...sans }}>

      {/* ─── HEADER ─── */}
      <div
        style={{
          background:     PURE_B,
          padding:        isMobile ? "28px 20px" : "36px 56px",
          display:        "flex",
          alignItems:     "center",
          justifyContent: "space-between",
          flexWrap:       "wrap",
          gap:            16,
        }}
      >
        <div>
          <div style={{ ...serif, fontSize: isMobile ? 24 : 30, fontWeight: 300, letterSpacing: "0.28em", color: PURE_W, lineHeight: 1 }}>
            COSMOOLA
          </div>
          <div style={{ ...sans, fontSize: 9, letterSpacing: "0.35em", color: WARMGRAY, marginTop: 6, textTransform: "uppercase" as const, fontWeight: 500 }}>
            Organisation Overview
          </div>
        </div>

        {/* Back to list */}
        <motion.button
          whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
          onClick={() => navigate(`/merchantdashboard/${user?.userId}`)}
          style={{ ...sans, padding: isMobile ? "10px 16px" : "12px 28px", border: `1.5px solid ${PURE_W}`, background: "transparent", color: PURE_W, fontWeight: 600, fontSize: 11, cursor: "pointer", letterSpacing: "0.14em", textTransform: "uppercase" as const, transition: "all 0.3s" }}
        >
          Go To Dashboard
        </motion.button>
      </div>

      {/* ─── CONTENT ─── */}
      <div style={{ maxWidth: 1000, margin: "0 auto", padding: isMobile ? "32px 16px" : "52px 40px" }}>

        {/* Loading */}
        {loading && !fetched && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ textAlign: "center", padding: 80, color: WARMGRAY, ...sans, fontSize: 14 }}>
            Loading your organisations...
          </motion.div>
        )}

        {/* Empty */}
        {fetched && merchants.length === 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} style={{ textAlign: "center", padding: isMobile ? "60px 20px" : "100px 40px" }}>
            <div style={{ width: 80, height: 80, borderRadius: "50%", border: `1.5px solid ${STONE}`, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 28, fontSize: 28, color: STONE }}>◇</div>
            <h2 style={{ ...serif, fontSize: isMobile ? 28 : 36, fontWeight: 300, color: NIGHT, margin: "0 0 12px", fontStyle: "italic" }}>No organisations found</h2>
            <p style={{ ...sans, color: DUSK, fontSize: 14, lineHeight: 1.7, fontWeight: 300, maxWidth: 420, margin: "0 auto 32px" }}>
              Register a business from the merchant onboarding flow to see it listed here.
            </p>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} onClick={() => navigate("/merchantonboarding")} style={{ ...sans, padding: "14px 40px", border: `2px solid ${PURE_B}`, background: PURE_B, color: PURE_W, fontWeight: 600, fontSize: 11, cursor: "pointer", letterSpacing: "0.18em", textTransform: "uppercase" as const, transition: "all 0.3s" }}>
              Go to Onboarding
            </motion.button>
          </motion.div>
        )}

        {/* Error banner */}
        <AnimatePresence>
          {(error || storeError) && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              style={{ marginBottom: 24, padding: "14px 20px", background: `${ROUGE}08`, border: `1px solid ${ROUGE}22`, ...sans, fontSize: 13, color: ROUGE, fontWeight: 400 }}
            >
              {error || storeError}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ─── MERCHANT CARDS ─── */}
        {merchants.length > 0 && (
          <div
            style={{
              display:             "grid",
              gridTemplateColumns: isMobile ? "1fr" : merchants.length === 1 ? "1fr" : "1fr 1fr",
              gap:                 isMobile ? 16 : 24,
            }}
          >
            {merchants.map((m, i) => (
      <MerchantCard
  key={m.merchantId}
  merchant={m}
  index={i}
  isMobile={isMobile}
  isGenerating={generatingId === m.merchantId}
  onGenerateKeys={() => handleGenerateKeys(m)}
  onSetUp={() => navigate(`/organizationdashboard/${m.merchantId}`)}
/>
            ))}
          </div>
        )}
      </div>

      {/* ─── KEYS MODAL ─── */}
      <AnimatePresence>
        {keysModal && (
          <motion.div
            variants={modalOverlay} initial="hidden" animate="visible" exit="exit"
            onClick={() => setKeysModal(null)}
            style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          >
            <motion.div
              variants={modalContent} initial="hidden" animate="visible" exit="exit"
              onClick={(e) => e.stopPropagation()}
              style={{ background: PURE_W, width: "100%", maxWidth: 600, maxHeight: "90vh", overflowY: "auto", padding: isMobile ? 24 : 40 }}
            >
              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
                <div>
                  <h2 style={{ ...serif, fontSize: isMobile ? 26 : 32, fontWeight: 400, color: NIGHT, margin: 0, lineHeight: 1.2 }}>Your API Keys</h2>
                  <div style={{ ...sans, fontSize: 11, color: WARMGRAY, marginTop: 6, letterSpacing: "0.06em" }}>
                    Account: <span style={{ fontFamily: "'Courier New', monospace", color: DUSK }}>{keysModal.accountId}</span>
                  </div>
                </div>
                <button
                  onClick={() => setKeysModal(null)}
                  style={{ background: "transparent", border: `1px solid ${STONE}`, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 16, color: DUSK, transition: "all 0.2s" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = PURE_B; e.currentTarget.style.color = PURE_B; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = STONE; e.currentTarget.style.color = DUSK; }}
                >✕</button>
              </div>

              {/* Warning */}
              <div style={{ background: "#FFF8E7", border: `1px solid ${AMBER}33`, padding: "14px 18px", marginBottom: 28, ...sans, fontSize: 13, color: "#7A6520", lineHeight: 1.7 }}>
                <strong>Save these keys now.</strong> Secret keys will never be shown again. Store them in a secure password manager immediately.
              </div>

              {/* Key rows */}
              {KEY_LABELS.map(({ label, key }) => {
                const value    = keysModal.keys[key] || "—";
                const isCopied = copied === key;
                return (
                  <div key={key} style={{ marginBottom: 16 }}>
                    <label style={{ ...sans, fontSize: 9, fontWeight: 600, letterSpacing: "0.16em", color: WARMGRAY, textTransform: "uppercase" as const, display: "block", marginBottom: 6 }}>
                      {label}
                    </label>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <div style={{ flex: 1, background: "#F8F6F3", border: `1px solid ${CREAM}`, padding: "11px 14px", fontFamily: "'Courier New', monospace", fontSize: 12, color: NIGHT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: "0.01em" }}>
                        {value}
                      </div>
                      <button
                        onClick={() => copyToClipboard(key, value)}
                        style={{ ...sans, padding: "11px 16px", border: `1px solid ${isCopied ? SAGE : STONE}`, background: isCopied ? `${SAGE}0A` : "transparent", color: isCopied ? SAGE : DUSK, fontSize: 11, fontWeight: 600, cursor: "pointer", letterSpacing: "0.06em", whiteSpace: "nowrap", transition: "all 0.2s" }}
                        onMouseEnter={(e) => { if (!isCopied) { e.currentTarget.style.borderColor = PURE_B; e.currentTarget.style.color = PURE_B; } }}
                        onMouseLeave={(e) => { if (!isCopied) { e.currentTarget.style.borderColor = STONE; e.currentTarget.style.color = DUSK; } }}
                      >
                        {isCopied ? "✓ Copied" : "Copy"}
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Done */}
              <button
                onClick={() => setKeysModal(null)}
                style={{ ...sans, width: "100%", marginTop: 12, padding: "16px 0", border: `2px solid ${PURE_B}`, background: PURE_B, color: PURE_W, fontWeight: 600, fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase" as const, cursor: "pointer", transition: "all 0.3s" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = PURE_B; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = PURE_B; e.currentTarget.style.color = PURE_W; }}
              >
                I've Saved My Keys
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ═══════════════════════════════════════
   MERCHANT CARD
   ═══════════════════════════════════════ */
function MerchantCard({
  merchant, index, isMobile, isGenerating, onGenerateKeys, onSetUp,
}: {
  merchant:       Merchant;
  index:          number;
  isMobile:       boolean;
  isGenerating:   boolean;
  onGenerateKeys: () => void;
  onSetUp:        () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const statusColor = merchant.status === "active" ? SAGE : merchant.status === "pending" ? AMBER : ROUGE;
  const hasKeys     = !!merchant.keysGenerated;

  return (
    <motion.div
      custom={index} variants={cardVariant} initial="hidden" animate="visible"
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ background: PURE_W, border: `1px solid ${hovered ? NIGHT : CREAM}`, padding: isMobile ? 24 : 32, cursor: "default", transition: "border-color 0.3s, box-shadow 0.3s", boxShadow: hovered ? "0 8px 32px rgba(0,0,0,0.06)" : "none", display: "flex", flexDirection: "column", gap: 20 }}
    >
      {/* Top: name + status */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ ...serif, fontSize: isMobile ? 22 : 26, fontWeight: 400, color: NIGHT, lineHeight: 1.2, letterSpacing: "-0.01em" }}>
            {merchant.businessName || "Untitled"}
          </div>
          <div style={{ ...sans, fontSize: 11, color: WARMGRAY, marginTop: 6, letterSpacing: "0.08em" }}>
            {merchant.businessType.charAt(0).toUpperCase() + merchant.businessType.slice(1)} · {merchant.country} · {merchant.currency.toUpperCase()}
          </div>
        </div>
        <div style={{ ...sans, fontSize: 9, fontWeight: 600, letterSpacing: "0.16em", textTransform: "uppercase" as const, color: statusColor, border: `1px solid ${statusColor}33`, padding: "5px 12px", background: `${statusColor}08` }}>
          {merchant.status}
        </div>
      </div>

      {/* Details grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <DetailCell label="Email"   value={merchant.email || "—"} />
        <DetailCell label="Mode"    value={merchant.mode === "live" ? "Live" : "Test"} />
        <DetailCell label="ID"      value={merchant.merchantId.length > 16 ? merchant.merchantId.slice(0, 16) + "..." : merchant.merchantId} mono />
        <DetailCell label="Keys"    value={hasKeys ? "Generated" : "Not generated"} color={hasKeys ? SAGE : AMBER} />
        {merchant.accountId && <DetailCell label="Account" value={merchant.accountId} mono />}
        <DetailCell label="KYC"     value={merchant.kycVerified ? "Verified" : "Pending"} color={merchant.kycVerified ? SAGE : WARMGRAY} />
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {/* Generate Keys — only shown if not yet generated */}
        {!hasKeys && (
          <motion.button
            whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}
            onClick={onGenerateKeys} disabled={isGenerating}
            style={{ ...sans, flex: 1, padding: "13px 0", border: `2px solid ${isGenerating ? STONE : NIGHT}`, background: isGenerating ? "#F8F6F3" : NIGHT, color: isGenerating ? WARMGRAY : PURE_W, fontWeight: 600, fontSize: 11, cursor: isGenerating ? "wait" : "pointer", letterSpacing: "0.16em", textTransform: "uppercase" as const, transition: "all 0.35s cubic-bezier(0.22,1,0.36,1)" }}
            onMouseEnter={(e) => { if (!isGenerating) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = NIGHT; } }}
            onMouseLeave={(e) => { if (!isGenerating) { e.currentTarget.style.background = NIGHT; e.currentTarget.style.color = PURE_W; } }}
          >
            {isGenerating ? "Generating..." : "Generate Keys"}
          </motion.button>
        )}

        {/* Dashboard / Set Up */}
<motion.button
  whileHover={{ scale: 1.01 }} 
  whileTap={{ scale: 0.98 }} 
  onClick={() => (`/organizationdashboard/${merchant.merchantId}`)}
  style={{ 
    ...sans, 
    flex: merchant.keysGenerated ? 1 : undefined, 
    minWidth: merchant.keysGenerated ? undefined : 120, 
    padding: "13px 24px", 
    border: `1.5px solid ${STONE}`, 
    background: "transparent", 
    color: DUSK, 
    fontWeight: 600, 
    fontSize: 11, 
    cursor: "pointer", 
    letterSpacing: "0.14em", 
    textTransform: "uppercase" as const, 
    transition: "all 0.3s" 
  }}
  onMouseEnter={(e) => { e.currentTarget.style.borderColor = NIGHT; e.currentTarget.style.color = NIGHT; }}
  onMouseLeave={(e) => { e.currentTarget.style.borderColor = STONE; e.currentTarget.style.color = DUSK; }}
>
  Dashboard →
</motion.button>



      </div>
    </motion.div>
  );
}

/* ─── Reusable detail cell ─── */
function DetailCell({ label, value, mono, color }: { label: string; value: string; mono?: boolean; color?: string }) {
  return (
    <div>
      <div style={{ ...sans, fontSize: 9, letterSpacing: "0.16em", color: STONE, textTransform: "uppercase" as const, fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ ...sans, fontSize: 12, color: color || DUSK, fontWeight: color ? 500 : 400, fontFamily: mono ? "'Courier New', monospace" : undefined, letterSpacing: mono ? "0.02em" : undefined }}>
        {value}
      </div>
    </div>
  );
}